import type {CSSProperties} from 'react';
import {font, radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeFormFieldVariant =
  | 'wedge-form-field' | 'wedge-sheet-field' | 'wedge-add-field' | 'wedge-add-field-lg' | 'wedge-add-field-sm'
  | 'wedge-answer-field' | 'wedge-settings-field' | 'wedge-search-field' | 'wedge-address-field'
  | 'wedge-onboard-field' | 'wedge-folder-field' | 'wedge-folder-side-field' | 'wedge-confirm-field'
  | 'wedge-domain-field' | 'wedge-bare-field' | 'wedge-deal-field' | 'wedge-deal-name-field' | 'wedge-saved-search-field' | 'wedge-search-bar-field' | 'wedge-command-field' | 'wedge-settingsdesk-domain-field' | 'wedge-emailc-subject-field' | 'wedge-emailc-add-field' | 'wedge-emailc-add-field-error' | 'wedge-business-field' | 'wedge-brief-find-field' | 'wedge-brief-find-field-open';

export const WEDGE_FORM_FIELD_VARIANTS: readonly WedgeFormFieldVariant[] = [
  'wedge-form-field', 'wedge-sheet-field', 'wedge-add-field', 'wedge-add-field-lg', 'wedge-add-field-sm',
  'wedge-answer-field', 'wedge-settings-field', 'wedge-search-field', 'wedge-address-field',
  'wedge-onboard-field', 'wedge-folder-field', 'wedge-folder-side-field', 'wedge-confirm-field',
  'wedge-domain-field', 'wedge-bare-field', 'wedge-deal-field', 'wedge-deal-name-field', 'wedge-saved-search-field', 'wedge-search-bar-field', 'wedge-command-field', 'wedge-settingsdesk-domain-field', 'wedge-emailc-subject-field', 'wedge-emailc-add-field', 'wedge-emailc-add-field-error', 'wedge-business-field', 'wedge-brief-find-field', 'wedge-brief-find-field-open',
];

export const isWedgeFormFieldVariant = (variant: unknown): variant is WedgeFormFieldVariant =>
  WEDGE_FORM_FIELD_VARIANTS.includes(variant as WedgeFormFieldVariant);

/* Released one-line text fields of the prototype, one look per cluster of live sites whose declared values match. Each is a
   bare native input. The values are the shipped inline styles, off-scale numbers included, and each color or family reads the
   exact legacy role, so the shipped ink rules and the host theme variables keep matching. A look carries width only when every
   live site declares it. Flex, the disabled dim and per-site margins stay with the caller. Focus and hover come from the shell
   sheet (`:focus-visible` always wins over the inline outline) and from the caller's own class, as before. */
export function wedgeFormFieldStyle(variant: WedgeFormFieldVariant, invalid = false): CSSProperties {
  const r = wedgeExactRole;
  const card = `1px solid ${r('cardBorder')}`;
  switch (variant) {
    /* Contact form fields, 44px tall. Invalid is the amber border only; the caller sets aria-invalid. */
    case 'wedge-form-field': return {
      width: '100%', minWidth: 0, height: 44, boxSizing: 'border-box', padding: '0 12px', borderRadius: radius.md,
      border: `1px solid ${r(invalid ? 'amber' : 'cardBorder')}`, background: r('bg'), color: r('label'),
      fontFamily: r('font'), fontSize: font.headline, outline: 'none',
    };
    /* Sheet forms: title, guest email, place. */
    case 'wedge-sheet-field': return {
      width: '100%', boxSizing: 'border-box', background: r('surface'), border: card, borderRadius: 9, padding: '10px 12px',
      color: r('label'), fontFamily: 'inherit', fontSize: font.body, outline: 'none',
    };
    /* Add-item rows: a field beside an Add button. Three released sizes. */
    case 'wedge-add-field': return {
      padding: '11px 13px', background: r('bgElev'), border: card, borderRadius: 11, fontSize: font.body,
      color: r('label'), fontFamily: r('font'), outline: 0, boxSizing: 'border-box',
    };
    case 'wedge-add-field-lg': return {
      padding: '10px 12px', background: r('bgElev'), border: card, borderRadius: 11, fontSize: font.headline,
      color: r('label'), fontFamily: r('font'), outline: 0, boxSizing: 'border-box',
    };
    case 'wedge-add-field-sm': return {
      padding: '10px 12px', borderRadius: radius.md, background: r('bgElev'), border: card, fontSize: 13.5,
      color: r('label'), fontFamily: r('font'), outline: 0,
    };
    /* Onboarding answer editors. No outline and no box sizing, as shipped; the fill color is pinned for WebKit. */
    case 'wedge-answer-field': return {
      background: r('surfaceAlt'), border: `1px solid ${r('separator')}`, borderRadius: radius.lg, padding: '12px 14px',
      color: r('label'), WebkitTextFillColor: r('label'), fontFamily: 'inherit', fontSize: font.headline, minHeight: 44,
    };
    /* Settings text fields. */
    case 'wedge-settings-field': return {
      width: '100%', padding: '11px 12px', background: r('bgElev'), border: card, borderRadius: 11, fontSize: 14.5,
      color: r('label'), fontFamily: r('font'), outline: 0, boxSizing: 'border-box',
    };
    /* Search with an icon at the left and a clear button at the right, both drawn in the padding. */
    case 'wedge-search-field': return {
      width: '100%', boxSizing: 'border-box', padding: '9px 34px 9px 32px', borderRadius: 11, background: r('canvas'),
      border: card, color: r('label'), fontFamily: r('font'), fontSize: font.headline, outline: 'none',
    };
    /* The Saved tab search: the Search look on the elevated solid fill in place of the canvas gradient. */
    case 'wedge-saved-search-field': return {
      width: '100%', boxSizing: 'border-box', padding: '9px 34px 9px 32px', borderRadius: 11, background: r('bgElev'),
      border: card, color: r('label'), fontFamily: r('font'), fontSize: font.headline, outline: 'none',
    };
    /* Address entry. The font is the shipped shorthand; no outline and no box sizing. */
    case 'wedge-address-field': return {
      padding: '9px 10px', borderRadius: radius.md, border: card, background: r('bg'), color: r('label'),
      font: `400 14px ${r('font')}`,
    };
    /* Large onboarding identity and phone fields, with the border and ring transition. */
    case 'wedge-onboard-field': return {
      width: '100%', padding: '14px 15px', background: r('bgElev'), border: card, borderRadius: 13, fontSize: 16.5,
      fontWeight: 600, color: r('label'), fontFamily: r('font'), outline: 0, boxSizing: 'border-box',
      transition: `border-color 200ms ${r('ease')}, box-shadow 200ms ${r('ease')}`,
    };
    /* Folder rename and new-folder fields. The pill is the live radius: the vertical branch of the source is never called. */
    case 'wedge-folder-field': return {
      boxSizing: 'border-box', padding: '7px 11px', borderRadius: radius.pill, background: r('bgElev'), color: r('label'),
      border: `0.5px solid ${r('accent')}`, outline: 'none', fontFamily: r('font'), fontSize: font.footnote,
    };
    case 'wedge-folder-side-field': return {
      width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 9, background: r('bgElev'), color: r('label'),
      border: `0.5px solid ${r('accent')}`, outline: 'none', fontFamily: r('font'), fontSize: font.footnote,
    };
    /* Business confirmation sheet. */
    case 'wedge-confirm-field': return {
      width: '100%', padding: '11px 13px', background: r('surface'), border: card, borderRadius: radius.lg, fontSize: 14.5,
      color: r('label'), fontFamily: r('font'), outline: 0, boxSizing: 'border-box',
    };
    /* Team domain entry in the mono face. The shorthand comes first so the family and size below override it. */
    case 'wedge-domain-field': return {
      font: 'inherit', fontFamily: r('mono'), fontSize: font.footnote, color: r('label'), background: r('bgElev'),
      border: card, borderRadius: radius.md, padding: '9px 11px',
    };
    /* A bare input inside a shell that draws the field. */
    case 'wedge-bare-field': return {
      border: 0, outline: 0, background: 'transparent', fontSize: 14.5, fontFamily: r('font'), color: r('label'),
    };
    /* The typed search bar input (Search, Type mode) inside the bar shell: the bare field at 16px. Flex and the inline min width stay with the caller. */
    case 'wedge-search-bar-field': return {
      border: 0, outline: 0, background: 'transparent', fontSize: 16, fontFamily: r('font'), color: r('label'),
    };
    /* The command palette search input: the bare field at 15px, medium weight. Flex and the inline min width stay with the caller. */
    /* The desktop Settings Join a team domain entry: 44px tall in the mono face on the elevated fill. Flex and the inline min width stay with the caller. */
    case 'wedge-settingsdesk-domain-field': return {
      minHeight: 44, boxSizing: 'border-box', font: 'inherit', fontFamily: r('mono'), fontSize: 14, color: r('label'),
      background: r('bgElev'), border: card, borderRadius: 12, padding: '10px 12px',
    };
    /* The email composer subject line: the bare field at 14.5px, semibold. Flex and the inline min width stay with the caller. */
    case 'wedge-emailc-subject-field': return {
      border: 0, outline: 0, background: 'transparent', fontSize: 14.5, fontWeight: 600, color: r('label'), fontFamily: r('font'), padding: 0,
    };
    /* The email composer add-address entry: 48px tall in the mono face on the elevated fill. The error look has the red border. */
    case 'wedge-emailc-add-field': case 'wedge-emailc-add-field-error': return {
      width: '100%', boxSizing: 'border-box', minHeight: 48, padding: '0 14px', borderRadius: 12,
      border: `1px solid ${r(variant === 'wedge-emailc-add-field-error' ? 'red' : 'separator')}`, background: r('bgElev'), color: r('label'),
      fontSize: 15, fontFamily: r('mono'), outline: 0,
    };
    case 'wedge-command-field': return {
      border: 0, outline: 0, background: 'transparent', fontSize: 15, color: r('label'), fontFamily: r('font'), fontWeight: 500,
    };
    /* The confirm business card fix input: full width, 14px, a bold separator border on the alternate fill, 9px radius. */
    case 'wedge-business-field': return {
      width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 9, border: `1px solid ${r('separatorBold')}`,
      background: r('bgElev'), color: r('label'), fontSize: 14, fontFamily: r('font'), outline: 'none',
    };
    /* The find field of a brief: a full width box on the elevated fill with a 10px radius and room for the search glyph at the left and the clear control at the right.
       The open look (the field has focus and the result list is out) takes the accent border. Flex and the inline min width stay with the caller. */
    case 'wedge-brief-find-field': case 'wedge-brief-find-field-open': return {
      width: '100%', boxSizing: 'border-box', padding: '8px 30px 8px 28px', borderRadius: 10, background: r('bgElev'), color: r('label'),
      border: variant === 'wedge-brief-find-field-open' ? `1px solid ${r('accent')}` : card, outline: 'none', fontFamily: r('font'), fontSize: 16,
    };
    /* Deal builder amounts (DealNumber): a full-width number field in bold mono type on the alternate fill, with the bold separator border. */
    case 'wedge-deal-field': return {
      width: '100%', fontSize: font.headline, fontFamily: r('mono'), fontWeight: weight.bold, color: r('label'),
      background: r('surfaceAlt'), border: `1px solid ${r('separatorBold')}`, borderRadius: radius.md, padding: '10px 12px', outline: 'none',
    };
    /* Deal builder name (DealBuilderScreen): the same box as the amounts, in semibold type. It declares no family, as shipped, so the shell sheet's input family applies. */
    case 'wedge-deal-name-field': return {
      width: '100%', fontSize: font.headline, fontWeight: weight.semibold, color: r('label'),
      background: r('surfaceAlt'), border: `1px solid ${r('separatorBold')}`, borderRadius: radius.md, padding: '10px 12px', outline: 'none',
    };
  }
}
